import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Icon from '@mdi/react'
import {
  mdiWhiteBalanceSunny,
  mdiCheckboxIntermediate,
  mdiInvertColors,
  mdiLayers,
  mdiRuler,
  mdiCursorDefault,
  mdiCursorPointer,
  mdiMagnify,
  mdiRefresh,
  mdiEyedropper,
  mdiAngleAcute,
  mdiCamera,
  mdiShareVariant,
  mdiClose,
} from '@mdi/js'
import { t } from '../i18n'
import { getJoystickSlots, JOYSTICK_MAX_SLOTS } from '../utils/joystickPrefs'
import WWPresetPanel from './WWPresetPanel'
import '../skins/joystick.css'

const ACTION_META = {
  Wwwc: { icon: mdiWhiteBalanceSunny, labelKey: 'menu.toolWwwc' },
  Magnify: { icon: mdiCheckboxIntermediate, labelKey: 'menu.toolMagnify' },
  Invert: { icon: mdiInvertColors, labelKey: 'menu.invert' },
  Zoom: { icon: mdiMagnify, labelKey: 'menu.toolZoom' },
  Pan: { icon: mdiCursorPointer, labelKey: 'menu.toolPan' },
  reset: { icon: mdiRefresh, labelKey: 'menu.resetImage' },
  series: { icon: mdiLayers, labelKey: 'joystick.series' },
  Length: { icon: mdiRuler, labelKey: 'menu.toolLength' },
  notool: { icon: mdiCursorDefault, labelKey: 'menu.toolNoTool' },
  Probe: { icon: mdiEyedropper, labelKey: 'menu.toolProbe' },
  Angle: { icon: mdiAngleAcute, labelKey: 'menu.toolAngle' },
  saveas: { icon: mdiCamera, labelKey: 'menu.captureViewport' },
  share: { icon: mdiShareVariant, labelKey: 'share.short' },
}

const LONG_PRESS_MS = 420
const PRESETS_SUPPRESS_MS = 500

/**
 * Menú radial ghost: click derecho / long-press (2 dedos si hay medición activa).
 * WW/WC: tap = herramienta; long-press = submenu de presets.
 */
function JoystickMenu({
  open,
  x = 0,
  y = 0,
  skin = 'classic',
  onClose,
  onSelect,
  slots,
  modality = null,
  presetsEnabled = true,
}) {
  const [presetsOpen, setPresetsOpen] = useState(false)
  const [presetsAnchor, setPresetsAnchor] = useState({ x: 0, y: 0 })
  const longPressTimer = useRef(null)
  const longPressFired = useRef(false)
  const wwwcSlotRef = useRef(null)
  const suppressBackdropUntil = useRef(0)

  const actions = useMemo(() => {
    const list = Array.isArray(slots) && slots.length ? slots : getJoystickSlots()
    return list.slice(0, JOYSTICK_MAX_SLOTS).map((id) => ({
      id,
      ...(ACTION_META[id] || ACTION_META.notool),
    }))
  }, [slots])

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const shouldSuppressBackdrop = () =>
    Date.now() < suppressBackdropUntil.current

  useEffect(() => {
    if (!open) {
      setPresetsOpen(false)
      clearLongPress()
      longPressFired.current = false
      suppressBackdropUntil.current = 0
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (presetsOpen) setPresetsOpen(false)
        else onClose && onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, presetsOpen])

  useEffect(() => () => clearLongPress(), [])

  if (!open) return null

  const n = actions.length || 1
  // Con 9 slots el radio sube para evitar solape de botones (44px).
  const radius = n >= 9 ? 96 : n >= 7 ? 88 : 78

  const openPresetsNear = (clientX, clientY) => {
    const ax =
      clientX != null
        ? clientX
        : wwwcSlotRef.current
          ? wwwcSlotRef.current.getBoundingClientRect().left + 22
          : x + 90
    const ay =
      clientY != null
        ? clientY
        : wwwcSlotRef.current
          ? wwwcSlotRef.current.getBoundingClientRect().top + 22
          : y
    suppressBackdropUntil.current = Date.now() + PRESETS_SUPPRESS_MS
    setPresetsAnchor({ x: ax, y: ay })
    setPresetsOpen(true)
  }

  const handleWwwcPointerDown = (e) => {
    longPressFired.current = false
    clearLongPress()
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (err) {
      /* ignore */
    }
    const cx = e.clientX
    const cy = e.clientY
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = null
      longPressFired.current = true
      openPresetsNear(cx, cy)
    }, LONG_PRESS_MS)
  }

  const handleWwwcPointerUp = (e) => {
    clearLongPress()
    if (longPressFired.current) {
      e.preventDefault()
      e.stopPropagation()
      // Mantener suppress hasta después del click/contextmenu del long-press
      suppressBackdropUntil.current = Date.now() + PRESETS_SUPPRESS_MS
      window.setTimeout(() => {
        longPressFired.current = false
      }, 0)
      return
    }
    onSelect && onSelect('Wwwc')
  }

  const handleWwwcClick = (e) => {
    if (presetsOpen || longPressFired.current || shouldSuppressBackdrop()) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const handleWwwcContextMenu = (e) => {
    // Long-press en touch dispara contextmenu; no cerrar ni propagar al root
    e.preventDefault()
    e.stopPropagation()
  }

  const handleBackdropDismiss = () => {
    if (shouldSuppressBackdrop()) return
    if (presetsOpen) {
      setPresetsOpen(false)
      return
    }
    onClose && onClose()
  }

  const node = (
    <div
      className={`udv-joystick-root udv-joystick--${skin}`}
      role="presentation"
      onClick={handleBackdropDismiss}
      onContextMenu={(e) => {
        e.preventDefault()
        handleBackdropDismiss()
      }}
    >
      <div
        className="udv-joystick"
        style={{ left: x, top: y }}
        role="menu"
        aria-label={t('joystick.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="udv-joystick-center"
          title={t('common.close')}
          aria-label={t('common.close')}
          onClick={onClose}
        >
          <Icon path={mdiClose} size={0.85} color="currentColor" />
        </button>
        {actions.map((action, i) => {
          const angle = (Math.PI * 2 * i) / n - Math.PI / 2
          const sx = Math.cos(angle) * radius
          const sy = Math.sin(angle) * radius
          const isWwwc = action.id === 'Wwwc'
          const wwwcPresets = presetsEnabled && isWwwc
          return (
            <button
              key={`${action.id}-${i}`}
              ref={isWwwc ? wwwcSlotRef : undefined}
              type="button"
              className={`udv-joystick-slot${wwwcPresets && presetsOpen ? ' is-presets-open' : ''}`}
              style={{
                transform: `translate(calc(-50% + ${sx}px), calc(-50% + ${sy}px))`,
              }}
              title={
                wwwcPresets
                  ? `${t(action.labelKey)} · ${t('joystick.wwwcLongPress')}`
                  : t(action.labelKey)
              }
              aria-label={t(action.labelKey)}
              onPointerDown={wwwcPresets ? handleWwwcPointerDown : undefined}
              onPointerUp={wwwcPresets ? handleWwwcPointerUp : undefined}
              onPointerCancel={wwwcPresets ? clearLongPress : undefined}
              onPointerLeave={
                wwwcPresets && !presetsOpen ? clearLongPress : undefined
              }
              onContextMenu={wwwcPresets ? handleWwwcContextMenu : undefined}
              onClick={
                wwwcPresets
                  ? handleWwwcClick
                  : () => onSelect && onSelect(action.id)
              }
            >
              <Icon path={action.icon} size={0.82} color="currentColor" />
            </button>
          )
        })}
      </div>

      {presetsEnabled && presetsOpen ? (
        <WWPresetPanel
          modality={modality}
          skin={skin}
          style={{
            left: presetsAnchor.x,
            top: presetsAnchor.y,
            transform: 'translate(12px, -50%)',
            zIndex: 3,
          }}
          onClick={(e) => e.stopPropagation()}
          onSelect={(preset) => {
            setPresetsOpen(false)
            onSelect && onSelect('Wwwc', { preset })
          }}
        />
      ) : null}
    </div>
  )

  return createPortal(node, document.body)
}

export default JoystickMenu
export { ACTION_META }
